import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, getOr, every } from 'lodash/fp';
import {
  Grid,
  Row,
  Col,
  Icon,
  Input,
  DateRangePicker,
  CheckPicker,
  Button
} from 'rsuite';
import { viewAccess } from '../../../helpers/permission';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import * as EXPORT_TYPES from '../../../constants/inventory/InventoryExportTypes';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import AccessControl from '../../AccessControl';
import ExportButton from '../../../containers/inventory/ExportCsvButton';
import SearchPanel from '../SearchPanel';

const ExportTypes = {
  [ItemType]: EXPORT_TYPES.EXPORT_ITEMS_AVAILABILITY,
  [PackageType]: EXPORT_TYPES.EXPORT_PACKAGES_AVAILABILITY
};

const PermisionByType = {
  [ItemType]: PERMISSIONS.EXPORT_ITEMS_AVAILABILITY,
  [PackageType]: PERMISSIONS.EXPORT_PACKAGES_AVAILABILITY
};

const AvailabilitySearch = ({
  type,
  searchQuery,
  tagsOptions,
  locationOptions,
  locationZoneOptions,
  onChange,
  onSubmit,
  onClear,
  onInit,
  isFetching,
  isFetchingTagsOptions,
  isFetchingLocationZoneOptions,
  isFetchingLocationOptions,
  isReport
}) => {
  useEffect(onInit, []);
  const handleOnChangeQuery = q => {
    onChange({ ...searchQuery, q });
  };
  const handleOnChangeCreatedAt = createdAt => {
    onChange({ ...searchQuery, createdAt });
  };
  const handleOnChangeTags = tagId => {
    onChange({ ...searchQuery, tagId });
  };
  const handleOnChangeLocation = inventoryLocationId => {
    onChange({ ...searchQuery, inventoryLocationId });
  };
  const handleOnChangeLocationZone = inventoryLocationZoneId => {
    onChange({ ...searchQuery, inventoryLocationZoneId });
  };
  const hasQuery = !every(isEmpty, searchQuery);
  return (
    <SearchPanel>
      <Grid fluid style={{ padding: 0 }}>
        <Row gutter={8}>
          <Col xs={24} md={6} lg={5}>
            <Input
              value={getOr('', 'q', searchQuery)}
              onChange={handleOnChangeQuery}
              style={{ paddingLeft: 10 }}
              placeholder="Search name or description"
              onKeyPress={({ key }) => {
                if (key === 'Enter') onSubmit(searchQuery);
              }}
            />
          </Col>
          {!isReport && (
            <Col xs={24} md={6} lg={4}>
              <DateRangePicker
                style={{ width: '100%' }}
                value={getOr([], 'createdAt', searchQuery)}
                placeholder="Select date range"
                onChange={handleOnChangeCreatedAt}
              />
            </Col>
          )}
          <Col xs={24} md={6} lg={4}>
            <CheckPicker
              style={{ width: '100%' }}
              data={tagsOptions}
              value={searchQuery.tagId || []}
              placeholder={
                isFetchingTagsOptions ? 'Loading tags' : 'Select tags'
              }
              onChange={handleOnChangeTags}
            />
          </Col>
          {type === ItemType && (
            <Col xs={24} md={6} lg={4}>
              <CheckPicker
                style={{ width: '100%' }}
                groupBy="location"
                data={locationZoneOptions}
                value={searchQuery.inventoryLocationZoneId || []}
                placeholder={
                  isFetchingLocationZoneOptions
                    ? 'Loading Location'
                    : 'Select Location'
                }
                onChange={handleOnChangeLocationZone}
              />
            </Col>
          )}
          {type === PackageType && (
            <Col xs={24} md={6} lg={4}>
              <CheckPicker
                style={{ width: '100%' }}
                data={locationOptions}
                value={searchQuery.inventoryLocationId || []}
                placeholder={
                  isFetchingLocationOptions
                    ? 'Loading Location'
                    : 'Select Location'
                }
                onChange={handleOnChangeLocation}
              />
            </Col>
          )}
          <Col sm={24} md={24} lg={7}>
            <Button
              style={{ borderBottom: 'none', marginRight: 10 }}
              color="green"
              disabled={!hasQuery || isFetching}
              onClick={() => onSubmit(searchQuery)}
            >
              <Icon icon="search" /> Search
            </Button>
            <Button
              style={{ borderBottom: 'none', marginRight: 10 }}
              disabled={isFetching}
              onClick={onClear}
            >
              <Icon icon="refresh" /> Reset
            </Button>
            <AccessControl allowOnlyFor={viewAccess(PermisionByType[type])}>
              {!isFetching && (
                <ExportButton
                  type={ExportTypes[type]}
                  searchQuery={searchQuery}
                />
              )}
            </AccessControl>
          </Col>
        </Row>
      </Grid>
    </SearchPanel>
  );
};

const SearchQueryType = PropTypes.shape({
  q: PropTypes.string,
  createdAt: PropTypes.arrayOf(PropTypes.shape({})),
  tagId: PropTypes.arrayOf(PropTypes.number),
  inventoryLocationId: PropTypes.arrayOf(PropTypes.number),
  inventoryLocationZoneId: PropTypes.arrayOf(PropTypes.number)
});

AvailabilitySearch.propTypes = {
  type: PropTypes.oneOf([ItemType, PackageType]).isRequired,
  searchQuery: SearchQueryType,
  tagsOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locationOptions: PropTypes.arrayOf(PropTypes.shape({})),
  locationZoneOptions: PropTypes.arrayOf(PropTypes.shape({})),
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onInit: PropTypes.func,
  isFetching: PropTypes.bool.isRequired,
  isFetchingTagsOptions: PropTypes.bool.isRequired,
  isFetchingLocationZoneOptions: PropTypes.bool,
  isFetchingLocationOptions: PropTypes.bool,
  isReport: PropTypes.bool
};

AvailabilitySearch.defaultProps = {
  searchQuery: {},
  locationOptions: [],
  locationZoneOptions: [],
  isFetchingLocationOptions: false,
  isFetchingLocationZoneOptions: false,
  onInit: () => {},
  isReport: false
};

export default AvailabilitySearch;
